import { useSelector } from 'react-redux';
import { ALI_UNDERSCORE } from '@rsos/constants/locationTypes';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';
import { isFordCallerID } from '@rsos/utils/isFordCallerID';

export const WPH1 = 'WPH1';
export const WPH2 = 'WPH2';
export const WRLS = 'WRLS';
export const MOBL = 'MOBL';
export const CELL = 'CELL';
export const OMBL = 'OMBL';
export const WCVC = 'WCVC';
export const WDL1 = 'WDL1';
export const WDL2 = 'WDL2';
export const SDXY = 'SDXY';
export const TEXT = 'TEXT';

/**
 * Custom hook to determine whether or not to render the Text-From-911 Chat
 * for a given phone number. If a phone number is considered a landline call,
 * do not render TF911. A phone number is considered a landline if its secondary
 * location data contains an `ani_` value for the `application`
 * @param {string|null|undefined} callerID - phone number
 */
const useShouldRenderTF911 = callerID => {
  // Throw an error if callerID is not provided to make this required for usage
  if (!callerID) {
    throw new Error('callerID not provided to useShouldRenderTF911');
  }
  const callQueue = useSelector(state => state.emergencyCalls.callQueue);
  const currentCall = callQueue[callerID];

  // Ford crash rows are VIN-keyed, not dialable — never offer TF911.
  if (isFordCallerID(callQueue, callerID)) {
    return false;
  }

  const mostRecentLocations = getMostRecentLocations(currentCall);
  const primaryLocation = mostRecentLocations.mostRecentLocation;
  const secondaryLocation = mostRecentLocations.mostRecentSecondaryLocation;

  // Name isn't accurate but using above comment as reasoning for name sense
  const isConsideredLandline =
    primaryLocation?.application?.startsWith(ALI_UNDERSCORE) ||
    secondaryLocation?.application?.startsWith(ALI_UNDERSCORE);

  const allowableClassOfServices = [
    WPH1,
    WPH2,
    WRLS,
    MOBL,
    CELL,
    OMBL,
    WCVC,
    WDL1,
    WDL2,
    SDXY,
    TEXT,
  ];
  const isSecondaryLocAllowed = !!allowableClassOfServices.includes(
    secondaryLocation?.class_of_service,
  );

  return !isConsideredLandline || isSecondaryLocAllowed;
};

export default useShouldRenderTF911;
